<template>
  <div id="app" :dir="isRtl">
    <router-view />
    <auth-sidebar />
    <notebook-sidebar />
    <worker-sidebar />
    <worker-sidebar-new />
    <company-sidebar />
    <jobs-sidebar />
<!--    <cookie-banner />-->
    <worker-contact-modal />
    <regulated-professions-modal />
    <force-change-password-modal />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import InformationNavigation from '@/components/Header/InformationNavigation.vue';
import PagesNavigation from '@/components/Header/PagesNavigation.vue';
import AuthSidebar from '@/components/Authorization/AuthSidebar.vue';
import NotebookSidebar from '@/components/Notebook/NotebookSidebar.vue';
import FooterComponent from '@/components/FooterComponent.vue';
import WorkerSidebar from '@/components/Workers/WorkerSidebar.vue';
import WorkerSidebarNew from '@/components/Workers/WorkerSidebarNew.vue';
import JobsSidebar from '@/components/Home/JobsSidebar.vue';
import RegulatedProfessionsModal from '@/components/Home/RegulatedProfessionsModal.vue';
import CompanySidebar from '@/components/Company/CompanySidebar.vue';
import CookieBanner from '@/components/CookieBanner.vue';
import WorkerContactModal from '@/components/Workers/WorkerContactModal.vue';
import ForceChangePasswordModal from '@/components/Authorization/ForceChangePasswordModal.vue';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import TemplatesMixin from '@/mixins/TemplatesMixin';

import { ActivityManager } from '@/plugins/activityManager';
import { NotebookModule } from './store/modules/notebook';

@Component({
  metaInfo(this: any): any {
    return {
      title: this.pageTitle,
    };
  },
  components: {
    InformationNavigation,
    PagesNavigation,
    FooterComponent,
    AuthSidebar,
    NotebookSidebar,
    WorkerSidebar,
    WorkerSidebarNew,
    JobsSidebar,
    CompanySidebar,
    CookieBanner,
    WorkerContactModal,
    RegulatedProfessionsModal,
    ForceChangePasswordModal,
  },
})
export default class App extends mixins(TemplatesMixin) {
  get languageSid() {
    return AppModule.languageSid;
  }

  get isRtl() {
    return AppModule.rtl;
  }

  // Pages without their own metaInfo (e.g. /cabinet) fall back to this title.
  // Prefer the PAT portal setting: /jobnews/settings html_title is stale and not
  // translated per language sid (amseu_ru → "Fachkräfteportal | AMS EURES").
  get pageTitle() {
    return AppModule.settingPortal.html_title || AppModule.setting.html_title;
  }

  get accessTokenApplicant() {
    return UserModule.accessTokenApplicant;
  }

  get accessTokenConsultant() {
    return UserModule.accessTokenConsultant;
  }

  @Watch('accessTokenApplicant')
  @Watch('accessTokenConsultant')
  onTokenChange() {
    this.startChat();
  }

  @Watch('$route', { deep: true })
  handler() {
    document.body.classList.remove('overflow-hidden');
  }

  startChat() {
    (window as any).ChatConfig = {
      sid: this.languageSid,
      useCookieAuth: false,
    };
    if (this.accessTokenApplicant) {
      (window as any).ChatConfig.accessToken = this.accessTokenApplicant;
    } else if (UserModule.accessTokenConsultant) {
      (window as any).ChatConfig.accessToken = UserModule.accessTokenConsultant;
    }
    (window as any).ChatWidget?.init();
  }

  directApply() {
    window.addEventListener('message', (event) => {
      if (event.data.event === 'direct_apply' && event.data.jobId) {
        if (window.dataLayer) {
          window.dataLayer.push(event.data);
        } else {
          console.log('no dataLayer');
        }
      }
    });
  }

  createFontsTemplate() {
    if (this.sid === 'jig') {
      this.jigCreateFontsUrl();
    } else if (this.sid === 'abaooe') {
      this.abaooeCreateFontsUrl();
    } else if ([
      'talentpool',
      'edunet',
      'wconat',
      'ncph',
      't4a'].includes(this.sid)) {
      this.talentpoolCreateFontsUrl();
    }
  }

  addEventListeners() {
    document.addEventListener('click', ActivityManager.updateLastActivity.bind(ActivityManager));
    document.addEventListener('keydown', ActivityManager.updateLastActivity.bind(ActivityManager));
  }

  removeEventListeners() {
    document.removeEventListener('click', ActivityManager.updateLastActivity.bind(ActivityManager));
    document.removeEventListener('keydown', ActivityManager.updateLastActivity.bind(ActivityManager));
  }

  handleLogout() {
    if (UserModule.accessTokenApplicant || UserModule.accessTokenConsultant || UserModule.accessTokenCompany) {
      UserModule.LogOutApplicant(this.sid);
      UserModule.LogOutConsultant(this.sid);
      UserModule.LogOutCompany(this.sid);
      this.$router.push({ name: 'Jobs', query: { lang: AppModule.language } }).catch(() => ({}));
    }
  }

  async created() {
    this.startChat();

    const htmlElement = document.documentElement;
    htmlElement.setAttribute('sid', this.sid);
    if (this.sid === 'talentpool' || this.sid === 'jig' || this.sid === 'aba' || this.sid === 'abatest') {
      htmlElement.setAttribute('theme', this.sid);
    } else {
      htmlElement.setAttribute('theme', 'default');
    }

    this.createCookieBanner();
    if (this.sid === 'abaooe') {
      this.abaooeCreateFontsUrl();
    }
    if (this.sid === 'amseu') {
      this.createIconUrlIco();
    }
    // this.createIconUrl();

    // this.createFontsTemplate();

    // START added User Activity
    ActivityManager.startInactivityTimer(this.handleLogout);
    this.addEventListeners();
    // END

    const loadTranslation = AppModule.getTranslation();
    await Promise.all([loadTranslation]);
    await AppModule.getSettingPortal({ sid: this.languageSid });
    await AppModule.getSetting({ sid: this.languageSid });
    await AppModule.getJobCountPortal({ sid: this.languageSid });

    if (UserModule.accessTokenApplicant) {
      if (!UserModule.userInfoApplicant) {
        await UserModule.GetUserInfoApplicant();
        // Merkliste прихована при обмеженому доступі — не тягнемо її дані
        if (UserModule.hasFullAccess) {
          await NotebookModule.getApplicantNotebookData();
        }
      }
    }
    if (UserModule.accessTokenConsultant) {
      if (!UserModule.userInfoConsultant) {
        await UserModule.GetUserInfoConsultant();
        await NotebookModule.getConsultantNotebookData();
      }
    }
    if (UserModule.accessTokenCompany) {
      if (!UserModule.userInfoCompany) {
        await UserModule.GetUserInfoCompany();
        await NotebookModule.getCompanyNotebookData();
      }
    }

    AppModule.SET_INITIAL_DATA_LOADED(true);

    this.directApply();
  }

  beforeDestroy(): void {
    this.removeEventListeners();
    ActivityManager.resetInactivityTimer();
  }
}
</script>
